import { useCallback } from 'react';
import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Share2, X, Trash2, Shield, UserPlus } from 'lucide-react';
import { shareFolder, getFolderShares, revokeShare, type ShareResponse } from '../lib/api';
import { useDosar } from '../context/DosarContext.shared';

interface ShareFolderModalProps {
    isOpen: boolean;
    onClose: () => void;
    folderId: number;
    folderName: string;
}

export const ShareFolderModal: React.FC<ShareFolderModalProps> = ({ isOpen, onClose, folderId, folderName }) => {
    const { showToast } = useDosar();
    const [email, setEmail] = useState('');
    const [permission, setPermission] = useState<'read' | 'write'>('read');
    const [shares, setShares] = useState<ShareResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [sharing, setSharing] = useState(false);



    const loadShares = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getFolderShares(folderId);
            setShares(data);
        } catch (e) {
            console.error("Failed to load shares", e);
        } finally {
            setLoading(false);
        }
    }, [folderId]);

useEffect(() => {
        if (isOpen && folderId) {
            loadShares();
        }
    }, [isOpen, folderId, loadShares]);

    const handleShare = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;

        try {
            setSharing(true);
            const newShare = await shareFolder(folderId, email, permission);
            // If exists, replace
            setShares(prev => {
                const filtered = prev.filter(s => s.shared_with_email !== email);
                return [...filtered, newShare];
            });
            setEmail('');
            showToast(`Invitație trimisă către ${email}`, 'success');
        } catch (e) {
            showToast((e as Error).message || 'Eroare la partajare.', 'error');
        } finally {
            setSharing(false);
        }
    };

    const handleRevoke = async (shareId: number) => {
        if (!confirm("Sigur vrei să revoci accesul?")) return;
        try {
            await revokeShare(shareId);
            setShares(prev => prev.filter(s => s.id !== shareId));
            showToast('Acces revocat.', 'info');
        } catch  {
            showToast('Eroare la revocare.', 'error');
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[160]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-ds-dialog bg-surface p-6 text-left align-middle shadow-overlay transition-all dark:bg-slate-800">
                                <div className="flex justify-between items-center mb-6">
                                    <Dialog.Title as="h3" className="text-xl font-bold leading-6 text-content dark:text-white flex items-center gap-2">
                                        <Share2 className="w-5 h-5 text-info" />
                                        Partajează "{folderName}"
                                    </Dialog.Title>
                                    <button onClick={onClose} className="text-content-muted hover:text-content-secondary dark:hover:text-content-muted">
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Share Form */}
                                <form onSubmit={handleShare} className="mb-8">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-content dark:text-content-muted mb-1">
                                                Email utilizator
                                            </label>
                                            <input
                                                type="email"
                                                required
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="exemplu@email.com"
                                                className="w-full rounded-ds-control border-border dark:border-border bg-surface dark:bg-slate-700 text-content dark:text-white focus:ring-focus focus:border-info/30 shadow-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-content dark:text-content-muted mb-1">
                                                Permisiuni
                                            </label>
                                            <div className="flex gap-4">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="permission"
                                                        value="read"
                                                        checked={permission === 'read'}
                                                        onChange={() => setPermission('read')}
                                                        className="text-info focus:ring-focus"
                                                    />
                                                    <span className="text-content dark:text-content-muted">Vizualizare</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="permission"
                                                        value="write"
                                                        checked={permission === 'write'}
                                                        onChange={() => setPermission('write')}
                                                        className="text-info focus:ring-focus"
                                                    />
                                                    <span className="text-content dark:text-content-muted">Editare</span>
                                                </label>
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={sharing}
                                            className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-ds-control shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-focus disabled:opacity-50"
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            {sharing ? 'Se trimite...' : 'Trimite Invitație'}
                                        </button>
                                    </div>
                                </form>

                                {/* Existing Shares List */}
                                <div>
                                    <h4 className="text-sm font-semibold text-content-secondary dark:text-content-muted uppercase tracking-wider mb-3">
                                        Acces curent
                                    </h4>

                                    {loading ? (
                                        <div className="text-center py-4 text-content-secondary">Se încarcă...</div>
                                    ) : shares.length === 0 ? (
                                        <div className="text-center py-4 text-content-secondary italic text-sm border border-dashed border-border dark:border-border rounded-ds-control">
                                            Acest dosar nu este partajat cu nimeni.
                                        </div>
                                    ) : (
                                        <ul className="space-y-3">
                                            {shares.map((share) => (
                                                <li key={share.id} className="flex items-center justify-between p-3 bg-surface-subtle dark:bg-slate-700/50 rounded-ds-control">
                                                    <div className="flex-1 min-w-0 mr-4">
                                                        <p className="text-sm font-medium text-content dark:text-white truncate">
                                                            {share.shared_with_email}
                                                        </p>
                                                        <div className="flex items-center gap-1 mt-0.5">
                                                            <Shield className="w-3 h-3 text-content-muted" />
                                                            <span className="text-xs text-content-secondary dark:text-content-muted capitalize">
                                                                {share.permission === 'read' ? 'Vizualizare' : 'Editare'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleRevoke(share.id)}
                                                        className="text-danger hover:text-danger p-1.5 hover:bg-danger/10 dark:hover:bg-red-900/20 rounded-ds-control transition-colors"
                                                        title="Revocă accesul"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="mt-6 pt-4 border-t border-border dark:border-border">
                                    <p className="text-xs text-center text-content-secondary dark:text-content-muted">
                                        Utilizatorii vor primi o notificare pe email.
                                    </p>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
